import React, { useEffect, useState } from "react";
import ReusableTable from "components/Tables/ReusableTable";
import { Flex, Box, Text, Badge } from "@chakra-ui/react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllNotificationsAction } from "state/actions/notificationActions";
import { EllipsisVertical, Plus, Telescope } from "lucide-react";
import ThreeDotMenu from "components/Menus/ThreeDotMenu.js";
import { truncateText } from "./utils/truncateText";
import GradientButton from "components/Buttons/GradientButton";
import ReusableDrawer from "components/Drawers/ReusableDrawer";
import AddNotificationDrawer from "./components/AddNotificationForm";
import ViewNotification from "./components/ViewNotification";

const allowedNotificationTypes = [
  "Customer Support",
  "Drivers",
  "Individuals",
  "Merchants",
  "Businesses",
  "General",
  "Security Alert",
];

const formatUserType = (type) => {
  if (type === "Individual") return "Individuals";
  return type + "s";
};

const NotificationManagement = () => {
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isViewNotificationDrawerOpen, setViewNotificationDrawerOpen] = useState(false);
  const [isAddNotificationOpen, setAddNotificationOpen] = useState(false);

  const dispatch = useDispatch();
  const allNotifications = useSelector((state) => state.notifications.allNotifications);
  const loading = useSelector((state) => state.notifications.loading);

  const [formData, setFormData] = useState({
    targetUsers: [],
    notificationType: "",
    message: "",
  });

  useEffect(() => {
    dispatch(fetchAllNotificationsAction());
  }, [dispatch]);

  const handleAddNotificationOpen = () => setAddNotificationOpen(true);

  const handleAddNotificationClose = () => {
    setAddNotificationOpen(false);
    setFormData({
      targetUsers: [],
      notificationType: "",
      message: "",
    });
  };

  const handleViewNotificationDetails = (notification) => {
    setSelectedNotification(notification);
    setViewNotificationDrawerOpen(true);
  };

  const handleCloseViewNotificationDrawer = () => {
    setSelectedNotification(null);
    setViewNotificationDrawerOpen(false);
  };

  const notificationsList = allNotifications?.results || [];

  const filteredNotifications = notificationsList.filter((notif) =>
    allowedNotificationTypes.includes(notif.notification_type)
  );

  const groupedMap = {};

  filteredNotifications.forEach((notif) => {
    const key = `${notif.message}-${notif.notification_type}`;

    if (!groupedMap[key]) {
      groupedMap[key] = {
        notificationId: notif.notification_id,
        message: notif.message,
        notificationType: notif.notification_type,
        targetUsers:
          notif.notification_user_types?.length > 0
            ? notif.notification_user_types.map((t) => t.user_type)
            : ["All"],
        status: notif.read_status === "Read" ? "Read" : "Sent",
        sentAt: notif.sent_at
          ? new Date(notif.sent_at).toLocaleString()
          : "-",
        count: 1,
      };
    } else {
      groupedMap[key].count += 1;
    }
  });

  const notificationData = Object.values(groupedMap);

  const formattedUsers = selectedNotification?.targetUsers
    ?.map((u) => formatUserType(u))
    .join(", ");

  const columnsData = [
    {
      Header: "Message",
      accessor: "message",
      Cell: ({ value }) => <Text>{truncateText(value)}</Text>,
    },
    {
      Header: "Type",
      accessor: "notificationType",
    },
    {
      Header: "Target Users",
      accessor: "targetUsers",
      Cell: ({ value }) => (
        <Text>
          {truncateText(
            value.map((t) => formatUserType(t)).join(", "),
            5
          )}
        </Text>
      ),
    },
    {
      Header: "Recipients",
      accessor: "count",
      Cell: ({ value }) => <Text>{value} users</Text>,
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <Badge
          colorScheme={
            value === "Sent"
              ? "green"
              : value === "Pending"
              ? "orange"
              : "gray"
          }
          borderRadius="full"
          px={2}
        >
          {value}
        </Badge>
      ),
    },
    {
      Header: "Sent At",
      accessor: "sentAt",
    },
    {
      Header: "Actions",
      accessor: "moreOptions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={[
            {
              icon: <Telescope size={16} />,
              text: "View Notification",
              onClick: () =>
                handleViewNotificationDetails(notificationData[row.index]), // 🔥 FIX
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "25px" }}>
      <Box p={6}>
        <Flex justifyContent="space-between" alignItems="center">
          {loading ? (
            <Skeleton height="20px" width="160px" />
          ) : (
            <Text fontSize="md" fontWeight="bold">
              Notifications: {notificationData.length}
            </Text>
          )}

          {loading ? (
            <Skeleton height="40px" width="150px" />
          ) : (
            <GradientButton
              icon={<Plus size={16} />}
              onClick={handleAddNotificationOpen}
              height="40px"
            >
              Send Notification
            </GradientButton>
          )}
        </Flex>
      </Box>

      <ReusableTable
        columnsData={columnsData}
        tableData={notificationData}
        isLoading={loading}
      />

      <ReusableDrawer
        isOpen={isAddNotificationOpen}
        onClose={handleAddNotificationClose}
        headerContent={<h2>Add Notification</h2>}
        bodyContent={
          <AddNotificationDrawer
            formData={formData}
            setFormData={setFormData}
            onClose={handleAddNotificationClose}
          />
        }
        size="md"
      />

      <ReusableDrawer
        isOpen={isViewNotificationDrawerOpen}
        onClose={handleCloseViewNotificationDrawer}
        headerContent={
          selectedNotification && (
            <h2>{`View Notification - to ${formattedUsers}`}</h2>
          )
        }
        bodyContent={
          selectedNotification && (
            <ViewNotification
              notification={selectedNotification}
              onClose={handleCloseViewNotificationDrawer}
            />
          )
        }
        size="lg"
      />
    </Flex>
  );
};

export default NotificationManagement;